// Per-project members management — self-service for project owners.
// Reuses the /api/admin/permissions endpoint (which is now scoped:
// project_admin can manage their own project, org_admin can manage
// org-wide bindings via the AdminPermissions page).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, KeyRound, AlertTriangle } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { useProjects } from '../api/client';
import {
  usePermissions,
  createPermission,
  deletePermission,
  type Permission,
} from '../api/permissions';
import { ApiError } from '../api/problem';

// project_admin can grant up to project_admin (cannot escalate to
// org_admin — backend enforces that too).
const ROLES: Permission['role'][] = ['reader', 'developer', 'maintainer', 'project_admin'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function roleTone(r: Permission['role']): 'accent' | 'nebula' | 'warning' | 'danger' | 'neutral' {
  switch (r) {
    case 'org_admin': return 'danger';
    case 'project_admin': return 'warning';
    case 'maintainer': return 'nebula';
    case 'developer': return 'accent';
    case 'reader': return 'neutral';
  }
}

export function ProjectMembers() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const { data: members, mutate, error } = usePermissions(id);
  const [target, setTarget] = useState<'user' | 'group'>('user');
  const [identity, setIdentity] = useState('');
  const [role, setRole] = useState<Permission['role']>('developer');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onAdd = async () => {
    if (!id || !identity.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createPermission({
        project_id: id,
        user_email: target === 'user' ? identity.trim() : undefined,
        group_name: target === 'group' ? identity.trim() : undefined,
        role,
      });
      setIdentity('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (mid: string) => {
    if (!confirm(t('projectMembers.confirmRemove'))) return;
    try {
      await deletePermission(mid);
    } catch (e) {
      alert(String(e));
    }
    await mutate();
  };

  const isForbidden = error instanceof ApiError && error.status === 403;

  return (
    <div className="space-y-6">
      <Link to={`/repos`} className="inline-flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
        <ArrowLeft className="h-4 w-4" />
        {t('projectMembers.back')}
      </Link>

      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">{t('projectMembers.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {project?.name ?? '…'}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          {t('projectMembers.intro')}
        </p>
      </div>

      {isForbidden && (
        <Card className="border-warning/40 bg-warning/5">
          <CardBody>
            <p className="text-sm text-fg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              {t('projectMembers.forbidden')}
            </p>
          </CardBody>
        </Card>
      )}

      {!isForbidden && (
        <>
          <Card>
            <CardHeader title={t('projectMembers.add.title')} description={t('projectMembers.add.description')} />
            <CardBody className="space-y-4">
              <fieldset className="border-0 p-0 m-0 min-w-0">
                <legend className="block text-sm font-medium text-fg mb-1.5">{t('projectMembers.add.target')}</legend>
                <div className="flex gap-2">
                  {(['user', 'group'] as const).map((targetKind) => (
                    <button
                      key={targetKind}
                      type="button"
                      onClick={() => setTarget(targetKind)}
                      className={
                        'inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm border transition-colors ' +
                        (target === targetKind
                          ? 'border-accent text-accent bg-accent/10'
                          : 'border-border text-fg-muted hover:text-fg')
                      }
                    >
                      {targetKind === 'user' ? <Users className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                      {targetKind === 'user' ? t('projectMembers.add.targetUser') : t('projectMembers.add.targetGroup')}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="member-identity" className="block text-sm font-medium text-fg mb-1.5">
                    {target === 'user' ? t('projectMembers.add.email') : t('projectMembers.add.groupName')}
                  </label>
                  <Input
                    id="member-identity"
                    value={identity}
                    onChange={(e) => setIdentity(e.target.value)}
                    placeholder={target === 'user' ? t('projectMembers.add.emailPlaceholder') : t('projectMembers.add.groupNamePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="member-role" className="block text-sm font-medium text-fg mb-1.5">{t('projectMembers.add.role')}</label>
                  <select
                    id="member-role"
                    className={selectClass + ' w-full'}
                    value={role}
                    onChange={(e) => setRole(e.target.value as Permission['role'])}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <p className="text-xs text-fg-subtle mt-1">
                    {t('projectMembers.add.roleHint')}
                  </p>
                </div>
              </div>
              {err && <p className="text-danger font-mono text-sm">{err}</p>}
            </CardBody>
            <CardFooter className="flex justify-end">
              <Button variant="primary" disabled={busy || !identity.trim()} onClick={onAdd}>
                <Plus className="h-4 w-4" />
                {t('projectMembers.add.submit')}
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader title={t('projectMembers.table.title', { count: members?.length ?? 0 })} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-5 py-3 font-medium text-fg-muted">{t('projectMembers.table.member')}</th>
                    <th className="px-5 py-3 font-medium text-fg-muted">{t('projectMembers.table.type')}</th>
                    <th className="px-5 py-3 font-medium text-fg-muted">{t('projectMembers.table.role')}</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {members?.length ? (
                    members.map((m) => (
                      <tr key={m.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                        <td className="px-5 py-3 text-fg">
                          {m.user_email || m.group_name}
                        </td>
                        <td className="px-5 py-3 text-fg-muted text-xs">
                          {m.user_email ? t('projectMembers.table.typeUser') : t('projectMembers.table.typeGroup')}
                        </td>
                        <td className="px-5 py-3">
                          <Badge tone={roleTone(m.role)}>{m.role}</Badge>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onRemove(m.id)}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-5 py-12 text-center text-fg-muted text-sm">
                        {t('projectMembers.table.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
